import React from 'react';
import { Star, ThumbsUp, ExternalLink, Shield } from 'lucide-react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import { REVIEW_SOURCES } from '../data/reviewSources';

// Google Icon SVG
const GoogleIcon = ({ size = 24 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size}>
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

// Justdial Icon SVG
const JustdialIcon = ({ size = 24 }) => (
  <svg viewBox="0 0 100 100" width={size} height={size}>
    <circle cx="50" cy="50" r="45" fill="#FFD700"/>
    <text x="50" y="65" textAnchor="middle" fontSize="40" fontWeight="bold" fill="#000">JD</text>
  </svg>
);

const Reviews = () => {
  const reviews = [
    {
      name: 'Rajesh Kumar',
      rating: 5,
      date: 'January 2026',
      text: 'Great place for dogs food great experience. The staff is very knowledgeable and helped me choose the best food for my Labrador. Highly recommended!',
      verified: true
    },
    {
      name: 'Priya Sharma',
      rating: 5,
      date: 'December 2025',
      text: 'They have a good variety of products at reasonable prices. Bought a Golden Retriever puppy from here and he is super healthy and playful. Thank you Petsmart!',
      verified: true
    },
    {
      name: 'Amit Verma',
      rating: 4,
      date: 'December 2025',
      text: 'Very nice service and good quality treatment. The puppies are well-cared for and vaccinated. Professional service.',
      verified: true
    },
    {
      name: 'Neha Gupta',
      rating: 5,
      date: 'November 2025',
      text: 'Excellent experience! Got my Shih Tzu from here. The team guided me through everything about puppy care. Very satisfied with the service.',
      verified: true
    },
    {
      name: 'Rohit Singh',
      rating: 5,
      date: 'November 2025',
      text: 'Trusted pet store in Noida! Wide range of products and healthy puppies. The staff is friendly and always ready to help. Will definitely recommend.',
      verified: true
    },
    {
      name: 'Sanya Malhotra',
      rating: 4,
      date: 'October 2025',
      text: 'Good variety of dog food brands. Prices are competitive. The store is clean and well-maintained.',
      verified: true
    },
    {
      name: 'Vikram Rao',
      rating: 5,
      date: 'October 2025',
      text: 'Amazing service! Bought a Beagle puppy and all necessary supplies. The team provided excellent after-sale support and care tips.',
      verified: true
    },
    {
      name: 'Anjali Kapoor',
      rating: 5,
      date: 'September 2025',
      text: 'Trustworthy and reliable. The puppies are healthy and well-trained. Great experience overall. Thank you for bringing joy to our family!',
      verified: true
    },
    {
      name: 'Karan Mehra',
      rating: 5,
      date: 'September 2025',
      text: 'Professional and caring staff. They helped me choose the right breed for my apartment. My French Bulldog is doing great!',
      verified: true
    }
  ];

  const google = REVIEW_SOURCES.google;
  const justdial = REVIEW_SOURCES.justdial;

  return (
    <>
      <SEO path="/reviews" />
      
      <div data-testid="reviews-page" style={{ backgroundColor: 'var(--neutral-bg)', minHeight: '100vh' }}>
        {/* Hero Section */}
        <section
          className="py-12 sm:py-16 lg:py-20"
          style={{
            background: 'linear-gradient(to bottom right, #FEF3C7, #FFFFFF)',
            borderBottom: '1px solid var(--neutral-border)'
          }}
        >
        <div className="container mx-auto px-4 text-center">
          {/* Verified Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4" style={{ backgroundColor: '#ECFDF5' }}>
            <Shield size={18} style={{ color: '#059669' }} />
            <span className="text-sm font-medium" style={{ color: '#059669' }}>Verified Reviews</span>
          </div>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" style={{ color: 'var(--text-main)' }}>
            Customer Reviews
          </h1>
          <p className="text-base sm:text-lg mb-8" style={{ color: 'var(--text-muted)' }}>
            See what pet parents are saying about us on Google and Justdial
          </p>

          {/* Rating Cards */}
          <div className="grid sm:grid-cols-2 gap-4 max-w-xl mx-auto mb-8">
            {/* Google Card */}
            <a
              href={google.profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-5 rounded-xl transition-all hover:shadow-lg hover:-translate-y-1"
              style={{ backgroundColor: 'white', border: '1px solid #E5E7EB' }}
            >
              <div className="flex items-center justify-center gap-2 mb-3">
                <GoogleIcon size={24} />
                <span className="font-semibold" style={{ color: '#1F2937' }}>Google</span>
              </div>
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="text-3xl font-bold" style={{ color: '#1F2937' }}>{google.rating}</span>
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} size={20} style={{ color: '#F59E0B', fill: star <= Math.floor(google.rating) ? '#F59E0B' : 'none' }} />
                  ))}
                </div>
              </div>
              <p className="text-sm" style={{ color: '#6B7280' }}>{google.reviewCount}+ reviews</p>
            </a>

            {/* Justdial Card */}
            <a
              href={justdial.listingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="p-5 rounded-xl transition-all hover:shadow-lg hover:-translate-y-1"
              style={{ backgroundColor: 'white', border: '1px solid #E5E7EB' }}
            >
              <div className="flex items-center justify-center gap-2 mb-3">
                <JustdialIcon size={24} />
                <span className="font-semibold" style={{ color: '#1F2937' }}>Justdial</span>
              </div>
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="text-3xl font-bold" style={{ color: '#1F2937' }}>{justdial.rating}</span>
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} size={20} style={{ color: '#F59E0B', fill: star <= Math.floor(justdial.rating) ? '#F59E0B' : 'none' }} />
                  ))}
                </div>
              </div>
              <p className="text-sm" style={{ color: '#6B7280' }}>{justdial.reviewCountDisplay} ratings</p>
            </a>
          </div>

          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
            <a
              href={google.profileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 transition-all hover:-translate-y-1"
              style={{
                backgroundColor: '#4285F4',
                color: 'white',
                padding: '0.875rem 1.5rem',
                borderRadius: '9999px',
                fontWeight: 'bold',
                boxShadow: '0 10px 30px rgba(66, 133, 244, 0.3)'
              }}
            >
              <GoogleIcon size={20} />
              View Google Reviews
            </a>
            <a
              href={justdial.listingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 transition-all hover:-translate-y-1"
              style={{
                backgroundColor: '#FFD700',
                color: '#1F2937',
                padding: '0.875rem 1.5rem',
                borderRadius: '9999px',
                fontWeight: 'bold',
                boxShadow: '0 10px 30px rgba(255, 215, 0, 0.3)'
              }}
            >
              <JustdialIcon size={20} />
              View Justdial Listing
            </a>
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-16">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="reviews-list">
            {reviews.map((review, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                viewport={{ once: true }}
                className="shadow-soft"
                style={{
                  backgroundColor: 'white',
                  padding: '1.5rem',
                  borderRadius: '1.5rem',
                  border: '1px solid var(--neutral-border)'
                }}
              >
                {/* Rating */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star
                        key={i}
                        size={18}
                        style={{ color: '#F59E0B', fill: '#F59E0B' }}
                      />
                    ))}
                  </div>
                  {review.verified && (
                    <span
                      className="text-xs px-2 py-1 rounded-full"
                      style={{
                        backgroundColor: 'var(--accent-light)',
                        color: 'var(--accent-main)',
                        fontWeight: '600'
                      }}
                    >
                      Verified
                    </span>
                  )}
                </div>

                {/* Review Text */}
                <p className="mb-4" style={{ color: 'var(--text-main)', lineHeight: '1.6' }}>
                  "{review.text}"
                </p>

                {/* Author */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-bold" style={{ color: 'var(--text-main)' }}>
                      {review.name}
                    </p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      {review.date}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Customer Photos Carousel */}
      <section className="py-16" style={{ backgroundColor: 'white' }}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: 'var(--text-main)' }}>
              Happy Customers & Their Puppies
            </h2>
            <p style={{ color: 'var(--text-muted)' }}>
              See our delighted customers with their new family members
            </p>
          </div>

          {/* Scrolling Container */}
          <div className="customer-carousel-wrapper" style={{ overflow: 'hidden', position: 'relative' }}>
            <div className="customer-carousel" data-testid="customer-carousel">
              {/* First set of images */}
              <div className="carousel-item">
                <img
                  src="https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/mgpebeyp_customer.jpeg"
                  alt="Happy customer with Husky puppy"
                  style={{
                    width: '300px',
                    height: '300px',
                    objectFit: 'cover',
                    borderRadius: '1rem',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.1)'
                  }}
                />
              </div>
              <div className="carousel-item">
                <img
                  src="https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/dldmkm4a_customer%203.jpeg"
                  alt="Happy customer with Dalmatian puppy"
                  style={{
                    width: '300px',
                    height: '300px',
                    objectFit: 'cover',
                    borderRadius: '1rem',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.1)'
                  }}
                />
              </div>
              <div className="carousel-item">
                <img
                  src="https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/njx2xp54_customer%204.jpeg"
                  alt="Happy customer with white puppy"
                  style={{
                    width: '300px',
                    height: '300px',
                    objectFit: 'cover',
                    borderRadius: '1rem',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.1)'
                  }}
                />
              </div>
              <div className="carousel-item">
                <img
                  src="https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/qx78q6q3_customer%205.jpeg"
                  alt="Happy couple with white puppy"
                  style={{
                    width: '300px',
                    height: '300px',
                    objectFit: 'cover',
                    borderRadius: '1rem',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.1)'
                  }}
                />
              </div>
              <div className="carousel-item">
                <img
                  src="https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/bxz3964b_customer%207.jpeg"
                  alt="Happy customer with golden retriever puppy"
                  style={{
                    width: '300px',
                    height: '300px',
                    objectFit: 'cover',
                    borderRadius: '1rem',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.1)'
                  }}
                />
              </div>

              {/* Duplicate set for seamless loop */}
              <div className="carousel-item">
                <img
                  src="https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/mgpebeyp_customer.jpeg"
                  alt="Happy customer with Husky puppy"
                  style={{
                    width: '300px',
                    height: '300px',
                    objectFit: 'cover',
                    borderRadius: '1rem',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.1)'
                  }}
                />
              </div>
              <div className="carousel-item">
                <img
                  src="https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/dldmkm4a_customer%203.jpeg"
                  alt="Happy customer with Dalmatian puppy"
                  style={{
                    width: '300px',
                    height: '300px',
                    objectFit: 'cover',
                    borderRadius: '1rem',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.1)'
                  }}
                />
              </div>
              <div className="carousel-item">
                <img
                  src="https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/njx2xp54_customer%204.jpeg"
                  alt="Happy customer with white puppy"
                  style={{
                    width: '300px',
                    height: '300px',
                    objectFit: 'cover',
                    borderRadius: '1rem',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.1)'
                  }}
                />
              </div>
              <div className="carousel-item">
                <img
                  src="https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/qx78q6q3_customer%205.jpeg"
                  alt="Happy couple with white puppy"
                  style={{
                    width: '300px',
                    height: '300px',
                    objectFit: 'cover',
                    borderRadius: '1rem',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.1)'
                  }}
                />
              </div>
              <div className="carousel-item">
                <img
                  src="https://customer-assets.emergentagent.com/job_petsmart-hub/artifacts/bxz3964b_customer%207.jpeg"
                  alt="Happy customer with golden retriever puppy"
                  style={{
                    width: '300px',
                    height: '300px',
                    objectFit: 'cover',
                    borderRadius: '1rem',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.1)'
                  }}
                />
              </div>
            </div>
          </div>

          {/* Carousel Styles */}
          <style>{`
            .customer-carousel-wrapper {
              position: relative;
              overflow: hidden;
              padding: 2rem 0;
            }

            .customer-carousel {
              display: flex;
              gap: 2rem;
              animation: scroll 30s linear infinite;
              width: fit-content;
            }

            .customer-carousel:hover {
              animation-play-state: paused;
            }

            .carousel-item {
              flex-shrink: 0;
              transition: transform 0.3s ease;
            }

            .carousel-item:hover {
              transform: scale(1.05);
            }

            @keyframes scroll {
              0% {
                transform: translateX(0);
              }
              100% {
                transform: translateX(-50%);
              }
            }

            @media (max-width: 768px) {
              .carousel-item img {
                width: 250px !important;
                height: 250px !important;
              }
            }
          `}</style>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16" style={{ backgroundColor: 'white' }}>
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <h3 className="text-4xl font-bold mb-2" style={{ color: 'var(--primary-main)' }}>
                199+
              </h3>
              <p style={{ color: 'var(--text-muted)' }}>Google Reviews</p>
            </div>
            <div>
              <h3 className="text-4xl font-bold mb-2" style={{ color: 'var(--accent-main)' }}>
                4.6
              </h3>
              <p style={{ color: 'var(--text-muted)' }}>Average Rating</p>
            </div>
            <div>
              <h3 className="text-4xl font-bold mb-2" style={{ color: 'var(--secondary-main)' }}>
                500+
              </h3>
              <p style={{ color: 'var(--text-muted)' }}>Happy Pet Parents</p>
            </div>
            <div>
              <h3 className="text-4xl font-bold mb-2" style={{ color: 'var(--primary-main)' }}>
                100%
              </h3>
              <p style={{ color: 'var(--text-muted)' }}>Healthy Puppies</p>
            </div>
          </div>
        </div>
      </section>
    </div>
    </>
  );
};

export default Reviews;